'use client'

import { useState } from 'react'
// TODO: Update from Base Comps
import {
  LabelDescription,
  LabelDescriptionProps,
} from '@/componentsV1/BaseComponents/Input'

import { Text, Checkbox, Popover, Separator, Input } from '@/ui'
import {
  GroupedDropdownValue,
  ParentDropdownOption,
  ChildDropdownOption,
  useGroupedDropdownLabel,
} from './groupedDropdownHelpers'

import { TypographyStyles, TextColors } from '@/styles'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons'
export interface GroupedDropdownProps<T extends GroupedDropdownValue> {
  options?: ParentDropdownOption<T>[]
  value?: T[]
  onChange?: (value: T[]) => void
  placeholder?: string
  disabled?: boolean
  error?: string
  label?: string
  description?: string
  required?: boolean
  afterLabel?: React.ReactNode
  fullWidth?: boolean
  allowOneParent?: boolean
  className?: string
}

export function GroupedDropdown<T extends GroupedDropdownValue>({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select...',
  disabled = false,
  error,
  label,
  description,
  required,
  afterLabel,
  allowOneParent = false,
  className,
}: GroupedDropdownProps<T> & LabelDescriptionProps) {
  const { childOptionMap, text } = useGroupedDropdownLabel(
    value ?? [],
    options ?? []
  )

  const [open, setOpen] = useState(false)

  const toggleValue = (val: T) => {
    if (disabled) return
    const exists = value.includes(val)
    let updated = [...value]

    if (exists) {
      updated = updated.filter((v) => v !== val)
    } else {
      if (allowOneParent) {
        const parent = childOptionMap[val]?.parent?.value
        updated = value.filter(
          (v) => childOptionMap[v]?.parent?.value === parent
        )
      }
      updated.push(val)
    }

    onChange?.(updated)
  }

  const toggleParent = (
    parentChecked: boolean,
    children: ChildDropdownOption<T>[]
  ) => {
    let updated = [...value]
    const childValues = children.map((c) => c.value)

    if (parentChecked) {
      updated = value.filter((val) => !childValues.includes(val))
    } else {
      if (allowOneParent) {
        updated = [...childValues]
      } else {
        updated = [...new Set([...value, ...childValues])]
      }
    }

    onChange?.(updated)
  }
  return (
    <div className={className}>
      <LabelDescription
        label={label}
        description={description}
        required={required}
        afterLabel={afterLabel}
      />

      <Popover open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <div className="relative w-full flex items-center">
            <Input
              role="combobox"
              type="text"
              readOnly
              disabled={disabled}
              value={typeof text === 'string' ? text : ''}
              placeholder={placeholder}
              className="cursor-pointer"
              iconRight={!disabled && value.length > 0 ? faXmark : faCaretDown}
            />
          </div>
        </Popover.Trigger>

        <Popover.Content
          align="start"
          sideOffset={4}
          forceMount
          className="absolute"
        >
          {options.map((group: ParentDropdownOption<T>) => {
            const allChecked = group.options.every(
              (opt: ChildDropdownOption<T>) => value.includes(opt.value)
            )

            return (
              <div key={group.value}>
                <span>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleParent(allChecked, group.options)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        toggleParent(allChecked, group.options)
                      }
                    }}
                    className="flex flex-row items-center mb-4"
                  >
                    <Checkbox checked={allChecked} disabled={disabled} />
                    <Text variant={TypographyStyles.BODY_MD} className="ml-2">
                      {group.text} (all)
                    </Text>
                  </div>
                  <Separator className="mb-4" />
                </span>
                {group.options.map((child) => (
                  <span key={child.value as string}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleValue(child.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          toggleValue(child.value)
                        }
                      }}
                      className="flex flex-row items-center ml-6 mb-4 mt-4"
                    >
                      <Checkbox
                        checked={value.includes(child.value)}
                        disabled={disabled}
                      />
                      <Text variant={TypographyStyles.BODY_MD} className="ml-2">
                        {child.text}
                      </Text>
                    </div>
                    <Separator className="mb-4" />
                  </span>
                ))}
              </div>
            )
          })}
        </Popover.Content>
      </Popover>

      {error && (
        <Text variant={TypographyStyles.BODY_MD} color={TextColors.DANGER}>
          {error}
        </Text>
      )}
    </div>
  )
}
